/**
 * Scroll targets for Schedule Interview communication step.
 * Section ids match EmailBlockSection `sectionId` in CandidatePane / InterviewerPane.
 */
import type { InterviewCommunicationValidationErrors } from '../../../InterviewActivity/communication/validate';

export type ScheduleCommunicationSectionKey =
  keyof InterviewCommunicationValidationErrors;

export type ScheduleCommunicationScrollTarget = {
  key: ScheduleCommunicationSectionKey;
  sectionId: string;
};

const SCHEDULE_ERROR_SCROLL_ORDER: Array<{
  key: ScheduleCommunicationSectionKey;
  sectionId: string;
}> = [
  { key: 'candidateCalendar', sectionId: 'schedule-comm-candidate-calendar' },
  { key: 'candidateEmail', sectionId: 'schedule-comm-candidate-email' },
  {
    key: 'interviewerCalendar',
    sectionId: 'schedule-comm-interviewer-calendar',
  },
  { key: 'interviewerEmail', sectionId: 'schedule-comm-interviewer-email' },
  { key: 'selfSchedule', sectionId: 'schedule-comm-self-schedule' },
];

export function getFirstScheduleCommunicationScrollTarget(
  errors: InterviewCommunicationValidationErrors
): ScheduleCommunicationScrollTarget | null {
  for (const { key, sectionId } of SCHEDULE_ERROR_SCROLL_ORDER) {
    if (errors[key]) {
      return { key, sectionId };
    }
  }
  return null;
}

export function scrollScheduleCommunicationSectionIntoView(
  sectionId: string
): void {
  const el = document.getElementById(sectionId);
  if (!el) return;

  // Find the nearest overflow-auto/scroll ancestor so we scroll within
  // the communication step's own container, not the window. Using
  // scrollIntoView({ block: 'start' }) escalates to outer containers
  // when the element is near the bottom, which shifts the footer.
  const scrollParent = getScrollParent(el);
  if (!scrollParent) {
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    return;
  }

  const parentRect = scrollParent.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const targetScrollTop =
    scrollParent.scrollTop + (elRect.top - parentRect.top) - 16;
  scrollParent.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
}

const MESSAGE_MARGIN_PX = 16;

/**
 * Bring the block's error MESSAGE back on screen after `focusFirstError()`.
 *
 * The kula Email renders ONE consolidated message for the whole block, as the
 * last child of the organism — below the bordered card, so below the editor.
 * `focusFirstError()` scrolls the offending FIELD's row instead
 * (`[data-kula-email-row]`, `block: 'center'`). Subject and body sit near the
 * bottom, so their message lands on screen anyway; From is the TOPMOST row, so
 * centring it parks the message a full editor-height below the fold and the
 * recruiter gets a red border with no reason for it.
 *
 * Moves the MINIMUM needed and is a no-op when the message is already visible,
 * so it cannot undo a scroll that already landed well. Computing an absolute
 * target from the live `scrollTop` keeps it correct mid-animation: the rect
 * offset shifts exactly opposite to `scrollTop`, so their sum is invariant.
 */
export function scrollScheduleCommunicationMessageIntoView(
  sectionId: string
): void {
  const el = document.getElementById(sectionId);
  if (!el) return;
  const scrollParent = getScrollParent(el);
  if (!scrollParent) return;

  const parentRect = scrollParent.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const hiddenBelow = elRect.bottom + MESSAGE_MARGIN_PX - parentRect.bottom;
  if (hiddenBelow <= 0) return;

  scrollParent.scrollTo({
    top: scrollParent.scrollTop + hiddenBelow,
    behavior: 'smooth',
  });
}

function getScrollParent(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    const { overflow, overflowY } = window.getComputedStyle(node);
    if (/auto|scroll/.test(overflow) || /auto|scroll/.test(overflowY)) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}
